import { enqueueSnackbar } from 'notistack';
import { useMemo, ChangeEvent, useCallback } from 'react';

import { useTheme } from '@mui/system';
import { Card, Table, TableBody, useMediaQuery, TableContainer } from '@mui/material';

import { useTranslate } from 'src/locales';
import { PageOptions, PaginatedResponse } from 'src/services/api.types';
import { JobApplication } from 'src/services/applications/applications.types';
import { useUpdateApplicationRatingMutation } from 'src/services/applications/applications.service';

import Scrollbar from 'src/components/scrollbar';
import {
  TableProps,
  TableNoData,
  TableSkeleton,
  TableHeadCustom,
  TableSelectedAction,
  TablePaginationCustom,
} from 'src/components/table';

import console from 'console';
import ApplicationsTableRow from './applications-table-row';

type Props = {
  loading?: boolean;
  table?: TableProps;
  tableData: PaginatedResponse<JobApplication> | undefined;
  pageOptions: PageOptions;
  onChangePage: (pageNumber: number) => void;
  onChangeRowsPerPage: (rows: number) => void;
  onSelectAllRows?: (checked: boolean) => void;
  onRatingChange?: (id: string, rating: number) => void;
};

export default function ApplicationsTable({
  loading,
  table,
  tableData,
  pageOptions,
  onChangePage,
  onChangeRowsPerPage,
  onRatingChange,
  onSelectAllRows,
}: Props) {

  const [updateApplicationRating, { isLoading: updatingRating }] = useUpdateApplicationRatingMutation();

  const { t } = useTranslate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const tableLabels = useMemo(
    () => isMobile ? [] : [
      {
        id: 'candidate_name',
        label: t('applications.table.labels.candidate_name'),
      },
      {
        id: 'stage',
        label: t('applications.table.labels.stage'),
      },
      {
        id: 'status',
        label: t('applications.table.labels.status'),
      },
      {
        id: 'rating',
        label: t('applications.table.labels.rating'),
      },
      {
        id: 'created_at',
        label: t('applications.table.labels.applied_on'),
      },
    ],
    [isMobile, t]
  );

  const applicationsRowData = useMemo(
    () =>
      tableData?.results.map((row) => ({
        id: row.id,
        jobId: row.job.id,
        candidate_name: `${row.attached_profile.first_name} ${row.attached_profile.last_name}`,
        stage: row.stage,
        status: row.status,
        rating: row.rating,
        rating_is_ai: row.rating_is_ai,
        created_at: row.created_at,
      })) ?? [],
    [tableData]
  );

  const notFound = !tableData?.count;

  const handleRowsPerPageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeRowsPerPage(parseInt(event.target.value, 10));
    },
    [onChangeRowsPerPage]
  );

  const handlePageChange = useCallback(
    (_event: any, pageNumber: number) => {
      onChangePage(pageNumber + 1);
    },
    [onChangePage]
  );

  const handleRatingChange = useCallback(async (id: string, rating: number) => {
    try {
      await updateApplicationRating({ applicationId: id, rating }).unwrap();

      if (onRatingChange) {
        onRatingChange(id, rating);
      }

      enqueueSnackbar(t('applications.api.update_rating.success'), { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar(t('applications.api.update_rating.default_error'), { variant: 'error' });
    }
  }, [updateApplicationRating, t, onRatingChange]);

  return (
    <Card>
      {
        loading ?
          (
            <TableSkeleton />
          ) : (
            <TableContainer>
              {table && (
                <TableSelectedAction
                  dense={table.dense}
                  numSelected={table.selected.length}
                  rowCount={tableData?.count ?? 0}
                  onSelectAllRows={
                    onSelectAllRows
                      ? (checked) => {
                        onSelectAllRows(checked);
                      }
                      : undefined
                  }
                />
              )}
              <Scrollbar>
                <Table size={table?.dense ? 'small' : 'medium'}>
                  <TableHeadCustom headLabel={tableLabels} onSelectAllRows={onSelectAllRows} />
                  <TableBody>
                    {applicationsRowData.map((row, index) => (
                      <ApplicationsTableRow
                        key={index}
                        row={row}
                        selected={table ? table.selected.includes(row.id) : false}
                        onRowSelected={table?.onSelectRow}
                        onRatingChange={handleRatingChange}
                      />
                    ))}

                    <TableNoData notFound={notFound} />
                  </TableBody>
                </Table>
              </Scrollbar>
              <TablePaginationCustom
                count={tableData?.count ?? 0}
                page={pageOptions.page}
                rowsPerPage={pageOptions.per_page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                dense={table?.dense}
                onChangeDense={table?.onChangeDense}
              />
            </TableContainer>
          )
      }
    </Card>
  );
}
